import _ from 'lodash';
import React, { FC, useState, useRef, HTMLAttributes, useEffect, useMemo } from 'react';
import { useToggle } from 'react-use-v13';
import ReactFlow, {
  ReactFlowProvider,
  Edge,
  Node,
  Handle,
  Position,
  Controls,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import theme from 'design-system/theme';
import dagre from 'dagre';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

import { Button } from 'design-system/Form';
import { Badge } from 'design-system';
import { makeStyles } from 'design-system/styles';

import getTypeDisplayName from '@scale/shared/get-type-display-name';
import { PipelineNode, PipelineResource } from '@scale/shared/pipelinev3/lookup';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { CommonPipelineNodeName } from '@scale/shared-types';
import { NodeData } from '@scale/shared/pipelinev3/types';
import { lookupNodeDef } from '@scale/temporal-workflows/task-pipeline/framework/registry/index';
import { Text } from 'design-system/Typography';
import { Typography, BaseIcon } from '@scale/scaleui';
import Link from 'next/link';

const NODE_WIDTH = 260;
const NODE_HEIGHT = 160;
const DIMMED_OPACITY = '0.1';

const useStyles = makeStyles({
  badgeRoot: {
    width: '100%',
  },
  badge: {
    padding: 0,
  },
  badgeHidden: {
    transform: 'scale(0) translate(50%, -50%) !important',
  },
  nodeDetailsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: '8px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  nodeDetailsLeftColumnHuman: {
    flexBasis: '30%',
    color: '#808080',
  },
  nodeDetailsLeftColumnNonHuman: {
    flexBasis: '30%',
    color: '#CCCCCC',
  },
  nodeDetailsRightColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '70%',
  },
  nodeDetails: {
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
    textAlign: 'left',
    fontFamily: theme.typography.fontFamily,
    pointerEvents: 'none',
    background: 'rgb(51, 51, 51)',
    color: 'rgb(255, 255, 255)',
    width: NODE_WIDTH,
    minHeight: NODE_HEIGHT,
    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 8px',
    borderRadius: '12px',
    padding: '0px 0px 12px',

    '& a': {
      pointerEvents: 'auto !important',
    },
  },
  nodeDetailsHeader: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    marginBottom: '12px',
    padding: '8px',
    borderRadius: '12px 12px 0 0',
  },
  nodeDetailsContent: {
    padding: '0 12px',
  },
  nodeDetailsTitle: {
    flexBasis: '70%',
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 600,
  },
  nodeDetailsStatus: {
    flexBasis: '30%',
    textAlign: 'center',
    opacity: 0.6,
  },
});

interface IPipelineNodeGraphProps extends HTMLAttributes<HTMLDivElement> {
  nodes: PipelineNode[];
  executionId?: string;
  resources?: PipelineResource[];
  highlightIds?: string[] | null;
  height?: string;
  nodesSelectable?: boolean;
  showSkipNode?: boolean;
  showHideInvalidatedNodes?: boolean;
  showAllNodes?: boolean;
  showSimplifiedGraphWarning?: boolean;
  // Used by history view to show warning when multiple executions are selected
  selectedExecutionCount?: number;
}

const EDGE_COLOR_MAP: Record<string, string> = {
  active: '#F39212',
  complete: '#29CD9C',
  execution_error: '#cc0000',
  invalid_execution: '#cc0000',
  resource: '#0e4d92',
};

const renderMetadataRow = (
  key: string,
  value: JSX.Element | string | object,
  isHumanStage: boolean,
  classes: any,
) => {
  // FIXME this is not the right way to do this
  const valueToRender = React.isValidElement(value)
    ? value
    : _.isString(value)
    ? value
    : JSON.stringify(value);
  return (
    <div key={key} className={classes.nodeDetailsRow}>
      <div
        className={
          isHumanStage ? classes.nodeDetailsLeftColumnHuman : classes.nodeDetailsLeftColumnNonHuman
        }
      >
        {key}
      </div>
      <div className={classes.nodeDetailsRightColumn}>{valueToRender}</div>
      <style jsx>{``}</style>
    </div>
  );
};

interface INodeDisplayInfo {
  color: string;
  statusText: string;
}

class GraphNode {
  id: string;
  node: any;
  forwardEdges: Set<string>;
  backwardEdges: Set<string>;
  totalDependencies: Set<string>;
  prunedBackwardEdges: Set<string>;
  transitionReason?: string;

  constructor(id: string, node: any) {
    this.id = id;
    this.forwardEdges = new Set();
    this.backwardEdges = new Set();
    this.node = node;
    this.transitionReason = _.get(node, 'input.transitionReason');
    this.totalDependencies = new Set();
    this.prunedBackwardEdges = new Set();
  }
}

const getNodeDisplayInfo = (node: PipelineNode): INodeDisplayInfo => {
  switch (node.status) {
    case 'complete':
      return { color: 'rgba(41, 205, 156, 0.4)', statusText: 'Completed' };
    case 'active':
      return { color: 'rgba(243, 146, 18, 0.4)', statusText: `In Progress` };
    case 'execution_error':
      return { color: 'rgba(217, 50, 78, 0.4)', statusText: 'Failed' };
    case 'invalid_execution':
      return { color: 'rgba(217, 50, 78, 0.4)', statusText: 'Invalid' };
    case 'canceled':
      return { color: 'rgba(65, 98, 117, 0.4)', statusText: 'Canceled' };
    default:
      return { color: 'rgba(128, 128, 128, 0.4)', statusText: 'Not Started' };
  }
};

const PipelineNodeNode: FC<any> = ({ id, data: node, type, selected }) => {
  const { isRemoAdmin } = useRemoAdminCtx();
  const { color, statusText } = getNodeDisplayInfo(node);
  const classes = useStyles();

  const toolUrl = `${isRemoAdmin ? '/remoadmin/tools' : '/corp'}/lookup`;

  let failedErrorMessage;
  if (node.status === 'execution_error' || node.status === 'invalid_execution') {
    try {
      failedErrorMessage = node.error.message;
    } catch (e) {
      failedErrorMessage = node.error;
    }
  }

  const isHumanStage = ['human'].includes(node.type);

  const name = node.nodeName;

  return (
    <div
      className={classes.nodeDetails}
      style={{ opacity: type.endsWith('Dim') ? DIMMED_OPACITY : '1' }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#fff' }} />
      <Handle type="source" position={Position.Right} style={{ background: '#fff' }} />
      <Badge
        classes={{
          root: classes.badgeRoot,
          badge: classes.badge,
          invisible: selected ? undefined : classes.badgeHidden,
        }}
        invisible={!selected}
        badgeContent={<FontAwesomeIcon icon="check" />}
        showZero={false}
        color="primary"
      >
        <div className={classes.nodeDetailsHeader} style={{ background: color }}>
          <div className={classes.nodeDetailsTitle}>Node: {_.startCase(name)}</div>
          <div className={classes.nodeDetailsStatus}> {statusText} </div>
        </div>
      </Badge>
      <div className={classes.nodeDetailsContent}>
        {renderMetadataRow('ID', <a href={`${toolUrl}/${id}`}>{id}</a>, isHumanStage, classes)}
        {failedErrorMessage &&
          renderMetadataRow('Error', failedErrorMessage, isHumanStage, classes)}
        {node.possibleIntermediateError &&
          renderMetadataRow('Last Error', node.possibleIntermediateError, isHumanStage, classes)}
        {renderMetadataRow('Node Type', getTypeDisplayName(node.type), isHumanStage, classes)}
        {node.attempt &&
          renderMetadataRow(
            'Attempt',
            <a href={`${toolUrl}/${node.attempt}`}>{node.attempt}</a>,
            isHumanStage,
            classes,
          )}
        {node.worker &&
          renderMetadataRow(
            'Worker',
            <a href={`${toolUrl}/${node.worker}`}>{node.worker}</a>,
            isHumanStage,
            classes,
          )}
        {(!_.isNil(node.reviewLevel) || !_.isNil(node.input?.reviewLevel)) &&
          renderMetadataRow(
            'Review Level',
            node.reviewLevel ?? node.input?.reviewLevel,
            isHumanStage,
            classes,
          )}
        {node.completedAt &&
          renderMetadataRow(
            'Duration',
            moment.duration(moment(node.completedAt).diff(moment(node.createdAt))).humanize(),
            isHumanStage,
            classes,
          )}
        {node.createdAt &&
          renderMetadataRow(
            'Started',
            moment.duration(moment(node.createdAt).diff(moment())).humanize(true),
            isHumanStage,
            classes,
          )}
        {node.completedAt &&
          renderMetadataRow(
            'Completed',
            moment.duration(moment(node.completedAt).diff(moment())).humanize(true),
            isHumanStage,
            classes,
          )}
      </div>
    </div>
  );
};

const PipelineResourceNode: FC<any> = ({ id, data: resource, type, selected }) => {
  const classes = useStyles();
  const { isRemoAdmin } = useRemoAdminCtx();

  const toolUrl = `${isRemoAdmin ? '/remoadmin/tools' : '/corp'}/lookup`;

  return (
    <div
      className={classes.nodeDetails}
      style={{ opacity: type.endsWith('Dim') ? DIMMED_OPACITY : '1' }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#fff' }} />
      <Handle type="source" position={Position.Right} style={{ background: '#fff' }} />
      <Badge
        classes={{
          root: classes.badgeRoot,
          badge: classes.badge,
          invisible: selected ? undefined : classes.badgeHidden,
        }}
        invisible={!selected}
        badgeContent={<FontAwesomeIcon icon="check" />}
        showZero={false}
        color="primary"
      >
        <div className={classes.nodeDetailsHeader} style={{ background: 'rgba(14, 77, 146, 0.4)' }}>
          <div className={classes.nodeDetailsTitle}>Resource: {resource.name}</div>
        </div>
      </Badge>
      <div className={classes.nodeDetailsContent}>
        {renderMetadataRow('ID', <a href={`${toolUrl}/${id}`}>{id}</a>, false, classes)}
        {resource.sourcePipelineResourceId &&
          renderMetadataRow(
            'Source Resource ID',
            resource.sourcePipelineResourceId,
            false,
            classes,
          )}
        {resource.sourcePipelineResourceKey &&
          renderMetadataRow(
            'Source Resource Key',
            resource.sourcePipelineResourceKey,
            false,
            classes,
          )}
        {resource.createdAt &&
          renderMetadataRow(
            'Created',
            moment.duration(moment(resource.createdAt).diff(moment())).humanize(true),
            false,
            classes,
          )}
      </div>
    </div>
  );
};

const generateDagre = (nodes: any[], edges: any[], highlightIds: string[] | null) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 100, ranksep: 100 });
  g.setDefaultEdgeLabel(function () {
    return {};
  });

  const nodeMap: Record<string, any> = {};
  for (const n of nodes) {
    g.setNode(n.id, { width: n.width, height: n.height });
    nodeMap[n.id] = n;
  }
  const edgesMap = new Map();
  for (const e of edges) {
    edgesMap.set(`${e.start}-${e.end}`, e);
    g.setEdge(e.start, e.end);
  }
  dagre.layout(g);

  const retNodes: Node[] = [];
  g.nodes().forEach(nid => {
    const { x, y } = g.node(nid);
    const { dagreType, ...data } = nodeMap[nid];
    retNodes.push({
      id: nid,
      type: dagreType,
      position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 },
      data,
    } as unknown as Node);
  });

  const retEdges: Edge[] = [];
  g.edges().forEach(e => {
    const edgeId = `${e.v}-${e.w}`;
    const edgeColor =
      e.v.startsWith('r-') || e.w.startsWith('r-')
        ? EDGE_COLOR_MAP.resource
        : EDGE_COLOR_MAP[nodeMap[e.v].status];
    const edgeOpacity =
      highlightIds === null
        ? '1'
        : highlightIds.includes(e.v) && highlightIds.includes(e.w)
        ? '1'
        : DIMMED_OPACITY;
    return retEdges.push({
      id: edgeId,
      source: e.v,
      target: e.w,
      arrowHeadType: 'arrow' as any,
      style: { stroke: edgeColor, strokeDasharray: 10, opacity: edgeOpacity },
      label: edgesMap.get(edgeId)?.label,
    } as unknown as Edge);
  });

  return { nodes: retNodes, edges: retEdges };
};

const getDagreType = (id: string, baseType: string, highlightIds: string[] | null) => {
  if (highlightIds === null) {
    return baseType;
  }
  if (highlightIds.includes(id)) {
    return baseType;
  }
  return `${baseType}Dim`;
};

const getNodesAndEdges = (
  pipelineNodes: PipelineNode[],
  pipelineResources: PipelineResource[],
  showAllNodes: boolean | null,
  highlightIds: string[] | null,
) => {
  const nodeNodes = pipelineNodes.map(n => ({
    ...n,
    width: NODE_WIDTH,
    height: NODE_HEIGHT,
    dagreType: getDagreType(n.id, 'pipelineNode', highlightIds),
    visibleOnPipelineGraph: lookupNodeDef(n.scope, n.nodeName).visibleOnPipelineGraph,
  }));
  const resourceNodes = pipelineResources.map(r => ({
    ...r,
    width: NODE_WIDTH,
    height: NODE_HEIGHT,
    dagreType: getDagreType(r.id, 'pipelineResource', highlightIds),
  }));

  let nodes = [...nodeNodes, ...resourceNodes];

  const validIds = nodes.map(n => n.id);
  const graph = new Map<string, GraphNode>();
  for (const n of nodes) {
    graph.set(n.id, new GraphNode(n.id, n));
  }
  for (const pn of pipelineNodes) {
    for (const depId of pn.dependencyIds) {
      if (validIds.includes(depId)) {
        graph.get(depId)?.forwardEdges.add(pn.id);
        graph.get(pn.id)?.backwardEdges.add(depId);
      }
    }
    for (const depId of pn.inputResourceIds) {
      if (validIds.includes(depId)) {
        graph.get(depId)?.forwardEdges.add(pn.id);
        graph.get(pn.id)?.backwardEdges.add(depId);
      }
    }
    for (const depId of pn.outputResourceIds) {
      if (validIds.includes(depId)) {
        graph.get(depId)?.forwardEdges.add(pn.id);
        graph.get(pn.id)?.backwardEdges.add(depId);
      }
    }
  }
  // Remove and propagate dependencies of nodes that are not visible on the pipeline graph
  if (!showAllNodes) {
    nodeNodes.forEach(n => {
      if (!n.visibleOnPipelineGraph) {
        const graphNode = graph.get(n.id);
        const beforeNodes = graphNode?.backwardEdges;
        const afterNodes = graphNode?.forwardEdges;
        if (!beforeNodes || !afterNodes) {
          return;
        }
        beforeNodes?.forEach(bn => {
          const beforeNode = graph.get(bn);
          afterNodes?.forEach(an => beforeNode?.forwardEdges.add(an));
          beforeNode?.forwardEdges.delete(n.id);
        });
        afterNodes?.forEach(an => {
          const afterNode = graph.get(an);
          beforeNodes?.forEach(bn => {
            afterNode?.backwardEdges.add(bn);
          });
          afterNode?.backwardEdges.delete(n.id);
        });
        graph.delete(n.id);
      }
    });
    const filteredNodes = nodeNodes.filter(n => n.visibleOnPipelineGraph);
    // Prune dependencies so that nodes only depend on direct dependencies
    const queue: string[] = _.filter(filteredNodes, n => !graph.get(n.id)?.backwardEdges.size).map(
      n => n.id,
    );
    const seen = new Set<string>();
    while (queue.length) {
      const current = queue.shift()!;
      if (seen.has(current)) {
        continue;
      }
      const currentNode = graph.get(current);
      if (!currentNode) {
        continue;
      }
      const allIndirectDependencies = new Set<string>();
      currentNode.backwardEdges.forEach(b => {
        const backwardNode = graph.get(b);
        currentNode.totalDependencies.add(b);
        backwardNode?.totalDependencies.forEach(d => allIndirectDependencies.add(d));
      });
      allIndirectDependencies.forEach(d => {
        currentNode.totalDependencies.add(d);
      });
      currentNode.backwardEdges.forEach(b => {
        if (!allIndirectDependencies.has(b)) {
          currentNode.prunedBackwardEdges.add(b);
        }
      });
      const forwardEdges = currentNode.forwardEdges;
      seen.add(current);

      forwardEdges.forEach(f => {
        const nextNode = graph.get(f);
        if (!nextNode) {
          return;
        }
        if (_.every(Array.from(nextNode.backwardEdges), b => seen.has(b))) {
          queue.push(f);
        }
      });
    }
    nodes = [...filteredNodes, ...resourceNodes];
  }

  const edges: { start: string; end: string; label?: string }[] = [];
  const nodesToShow = new Set(nodes.map(n => n.id));
  // Generate edges
  for (const n of graph.keys()) {
    if (!nodesToShow.has(n)) {
      continue;
    }
    const graphNode = graph.get(n);
    let backward;
    if (showAllNodes) {
      backward = graphNode?.backwardEdges;
    } else {
      backward = graphNode?.prunedBackwardEdges;
    }
    backward?.forEach(b => {
      if (!nodesToShow.has(b)) {
        return;
      }
      let edge: { start: string; end: string; label?: string } = { start: b, end: n };
      const node = graph.get(n);
      if (node?.transitionReason) {
        edge = { ...edge, label: node?.transitionReason };
      }
      edges.push(edge);
    });
  }
  return { nodes, edges };
};

const PipelineNodeGraph: FC<IPipelineNodeGraphProps> = ({
  nodes,
  executionId = null,
  resources = [],
  highlightIds = null,
  height = '575px',
  className,
  showAllNodes = true,
  showSimplifiedGraphWarning = true,
  nodesSelectable = false,
  showSkipNode = false,
  showHideInvalidatedNodes,
  selectedExecutionCount,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInteractive, setIsInteractive] = useState(false);
  const [corpJobId, setCorpJobId] = React.useState<string | null>();
  const handleBlur = () => setIsInteractive(false);
  const handleFocus = () => setIsInteractive(true);

  const [highlightingHumanNodes, toggleHighlightingHumanNodes] = useToggle(false);
  const [hideInvalidatedNodes, toggleHideInvalidatedNodes] = useToggle(false);
  const nonInvalidatedNodeIds = nodes.filter(n => !n.invalidatedAt).map(n => n.id);
  const nonInvalidatedResourceIds = resources
    .filter(r =>
      (r.inputTo ?? [])
        .concat(r.outputOf ?? [])
        .some(node => nonInvalidatedNodeIds.includes(node.toString())),
    )
    .map(r => r.id);
  const nonInvalidatedNodeAndResourceIds = nonInvalidatedNodeIds.concat(nonInvalidatedResourceIds);
  const [currentHighlightIds, setCurrentHighlightIds] = useState<string[] | null>(highlightIds);
  useEffect(() => {
    setCurrentHighlightIds(highlightIds);
  }, [highlightIds]);
  const humanNodeIds = nodes.filter(n => n.type === 'human').map(n => n.id);

  const [selectedNodeDataArray, setSelectedNodeDataArray] = useState<NodeData[]>([]);

  const recomputeGraph = () => {
    const { nodes: graphNodes, edges: graphEdges } = getNodesAndEdges(
      nodes,
      resources,
      showAllNodes,
      currentHighlightIds,
    );
    return generateDagre(graphNodes, graphEdges, currentHighlightIds);
  };
  const { nodes: initialNodes, edges: initialEdges } = useMemo(recomputeGraph, []);
  const [dagreNodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [dagreEdges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  useEffect(() => {
    const graph = recomputeGraph();
    setNodes(graph.nodes);
    setEdges(graph.edges);
    // This fixes the issue where if showAllNodes change the graph wasn't updating
  }, [currentHighlightIds, showAllNodes]);

  const onSelectionChange = (elements: { nodes: Node[]; edges: Edge[] }) => {
    if (!elements) {
      return;
    }
    const nodeIds: string[] = [];
    elements.nodes.map(elem => (elem.id[0] === 'n' ? nodeIds.push(elem.id) : null));

    const nodeDataArray: NodeData[] = [];
    elements.nodes.map(elem =>
      elem.id[0] === 'n'
        ? nodeDataArray.push({
            id: elem.data.id,
            name: elem.data.nodeName,
            taskId: elem.data.taskId,
            status: elem.data.status,
          })
        : null,
    );
    const updatedNodeDataArray = _.compact(nodeDataArray);
    if (!_.isEqual(selectedNodeDataArray, updatedNodeDataArray)) {
      setSelectedNodeDataArray(updatedNodeDataArray);
    }
  };

  const nodeTypes = useMemo(
    () => ({
      pipelineNode: PipelineNodeNode,
      pipelineNodeDim: PipelineNodeNode,
      pipelineResource: PipelineResourceNode,
      pipelineResourceDim: PipelineResourceNode,
    }),
    [],
  );

  return (
    <div
      className={className}
      ref={containerRef}
      style={{
        cursor: isInteractive ? 'grab' : 'initial',
        boxShadow: isInteractive ? 'inset 0px 0px 6px #ccc' : 'none',
        height,
        display: 'flex',
        flexDirection: 'column',
      }}
      onBlur={handleBlur}
      onFocus={handleFocus}
      tabIndex={1}
    >
      <ReactFlowProvider>
        <ReactFlow
          minZoom={0.25}
          maxZoom={1.25}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={true}
          panOnDrag={isInteractive}
          nodes={dagreNodes}
          edges={dagreEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          onSelectionChange={onSelectionChange}
          style={{ width: '100%' }}
          fitView={true}
        >
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>

      {/* allow highlighting only human nodes when no highlightIds prop passed in */}
      {!!(!highlightIds && humanNodeIds.length) && (
        <div style={{ marginBottom: 10 }}>
          <Button
            type="secondary"
            textTransform="none"
            onClick={() => {
              highlightingHumanNodes
                ? setCurrentHighlightIds(null)
                : setCurrentHighlightIds(humanNodeIds);
              toggleHighlightingHumanNodes();
              hideInvalidatedNodes && toggleHideInvalidatedNodes();
            }}
          >
            {highlightingHumanNodes ? 'Show All Nodes' : 'Highlight Human Nodes'}
          </Button>
        </div>
      )}
      {showHideInvalidatedNodes && nodes.filter(n => n.invalidatedAt).length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <Button
            type="secondary"
            textTransform="none"
            onClick={() => {
              hideInvalidatedNodes
                ? setCurrentHighlightIds(null)
                : setCurrentHighlightIds(nonInvalidatedNodeAndResourceIds);
              toggleHideInvalidatedNodes();
              highlightingHumanNodes && toggleHighlightingHumanNodes();
            }}
          >
            {hideInvalidatedNodes ? 'Show All Nodes' : 'Hide Invalidated Nodes'}
          </Button>
        </div>
      )}
      <Button
        type="danger"
        requireConfirmation={true}
        textTransform="none"
        onClick={async () => {
          if (selectedNodeDataArray.length < 1) {
            alert('No nodes have been selected to invalidate.');
            return;
          }
          if (selectedExecutionCount && selectedExecutionCount > 1) {
            alert('Please only select one execution when running batch invalidate.');
            return;
          }
          if (!executionId) {
            alert(
              'This node graph does not have an associated pipelineExecutionID. Please only run batch invalidate from task or pipelineExecution lookup',
            );
            return;
          }
          try {
            const result = await apiFetch(
              null,
              // Ideally this would be the pipelineExecution id but not sure if worth wiring up
              `corp-api/lookup/${executionId}/runCommand/taskPipelineV3BatchInvalidate`,
              {
                method: 'POST',
                body: {
                  selectedNodeIds: selectedNodeDataArray.map(node => node.id),
                  taskId: selectedNodeDataArray[0].taskId,
                },
              },
            );
            setCorpJobId(result.corpJobId);
            alert(JSON.stringify(result));
          } catch (error) {
            defaultApiFetchErrorHandler(error, { handleHttp: true });
          }
        }}
      >
        {'Batch Invalidate Selected Nodes'}
      </Button>
      {corpJobId && (
        <div className="flex flex-row gap-1 items-center">
          <Typography.Overline3 className="flex flex-row gap-1 items-center">
            Last job:
            <Link href={`${window.location.origin}/corp/lookup/${corpJobId}`} legacyBehavior>
              <a
                className="scaleui scale-theme scale-tailwind flex gap-1 items-center text-primary-400 "
                target="_blank"
              >
                {corpJobId}
                <BaseIcon
                  icon={['fas', 'external-link-alt']}
                  contrastText
                  edge="end"
                  variant="secondary"
                />
              </a>
            </Link>
          </Typography.Overline3>
        </div>
      )}
      {showSkipNode && (
        <Button
          type="danger"
          requireConfirmation={true}
          textTransform="none"
          onClick={async () => {
            if (selectedNodeDataArray.length < 1) {
              alert('No nodes have been selected to skip.');
              return;
            }
            if (selectedNodeDataArray.length > 1) {
              alert('Please select only one node to skip.');
              return;
            }
            if (
              ![CommonPipelineNodeName.OcrModel, CommonPipelineNodeName.Prelabel].includes(
                selectedNodeDataArray[0].name,
              )
            ) {
              alert(
                `Please select an OCR or Prelabel node to skip. (selected: ${selectedNodeDataArray[0].name})`,
              );
              return;
            }
            if (selectedNodeDataArray[0].status === 'complete') {
              alert(`Please select a node that has not completed.`);
              return;
            }
            if (!executionId) {
              alert(
                'This node graph does not have an associated pipelineExecutionID. Please only run skip nodes from Task lookup',
              );
              return;
            }
            try {
              await apiFetch(
                null,
                `corp-api/lookup/${executionId}/runCommand/taskPipelineV3SkipNodes`,
                {
                  method: 'POST',
                  body: { selectedNodeDataArray },
                },
              );
              alert(`Skipped node ${selectedNodeDataArray[0].id}. Please refresh the page.`);
            } catch (error) {
              defaultApiFetchErrorHandler(error, { handleHttp: true });
            }
          }}
        >
          {'Skip Selected Node'}
        </Button>
      )}
      {!showAllNodes && showSimplifiedGraphWarning && (
        <div className="center-text">
          {' '}
          <Text
            medium
          >{`This is a simplified pipeline node graph that only displays humanNodes, consensusNodes and finalizationNodes. To see the complete graph, expand the "Full Pipeline Execution Overview Graph" Below`}</Text>{' '}
        </div>
      )}
      <style jsx global>{`
        .react-flow__node {
          cursor: ${nodesSelectable ? 'pointer' : 'initial'};
          border: none;
        }

        .react-flow__edges {
          pointer-events: none !important;
        }
        .react-flow__edge-path {
          stroke-width: 1.5;
        }

        .react-flow__handle {
          visibility: hidden;
        }
        .react-flow__handle-right {
          right: 0;
        }
        .react-flow__handle-left {
          left: 0;
        }
        .react-flow__handle-top {
          top: 0;
        }
        .react-flow__handle-bottom {
          bottom: 0;
        }
        .center-text {
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default PipelineNodeGraph;
